import { ConfirmationNumber as ConfirmationIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { Chip, Stack, Tooltip } from "@mui/material";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import type { FC } from "react";
import { useState } from "react";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import dayjs from "../../dayjs";
import { useOrderConfirmations } from "./useOrderConfirmations";

interface Acceptance {
  acceptedAt: string;
  acceptedBy: { email: string; nameAndSurname: string };
}

interface ConfirmationChipProps {
  filename: string;
  url: string;
  acceptance: Acceptance | null;
  canDelete: boolean;
  onDelete: () => void | Promise<void>;
}

const ConfirmationChip: FC<ConfirmationChipProps> = ({ filename, url, acceptance, canDelete, onDelete }) => {
  const download = async () => {
    const storage = getStorage();
    const signedUrl = await getDownloadURL(ref(storage, url));
    window.open(signedUrl, "_blank");
  };

  const color = acceptance ? "success" : "primary";

  if (acceptance) {
    return (
      <Tooltip
        title={`Zaakceptowane ${dayjs(acceptance.acceptedAt).format("DD.MM.YYYY HH:mm")} przez ${
          acceptance.acceptedBy.nameAndSurname
        } (${acceptance.acceptedBy.email})`}
      >
        <Chip
          label={filename}
          variant="outlined"
          color={color}
          onClick={(e) => {
            e.stopPropagation();
            return download();
          }}
          icon={<ConfirmationIcon />}
          deleteIcon={<DeleteIcon />}
          onDelete={canDelete ? onDelete : undefined}
        />
      </Tooltip>
    );
  }

  return (
    <Chip
      label={filename}
      variant="outlined"
      color={color}
      onClick={(e) => {
        e.stopPropagation();
        return download();
      }}
      icon={<ConfirmationIcon />}
      deleteIcon={<DeleteIcon />}
      onDelete={canDelete ? onDelete : undefined}
    />
  );
};

interface Props {
  orderId: string;
  readonly?: boolean;
}

const Confirmations: FC<Props> = ({ orderId, readonly }) => {
  const { confirmations, deleteConfirmation } = useOrderConfirmations(orderId);
  const [confirmationDialog, setConfirmationDialog] = useState<{
    content: string;
    onConfirm: () => Promise<void>;
  }>();

  if (confirmations.length === 0) {
    return null;
  }

  const handleRemoveConfirmation = async (id: string, name: string) => {
    setConfirmationDialog({
      content: `Usunąć potwierdzenie '${name}' ?`,
      onConfirm: async () => {
        await deleteConfirmation(id);
        setConfirmationDialog(undefined);
      },
    });
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        flexWrap: "wrap",
      }}
    >
      {confirmations.map((c) => (
        <ConfirmationChip
          key={c.id}
          filename={c.name}
          url={c.url}
          acceptance={c.acceptance ?? null}
          canDelete={!readonly}
          onDelete={() => handleRemoveConfirmation(c.id, c.name)}
        />
      ))}
      {!readonly && (
        <ConfirmationDialog
          open={!!confirmationDialog}
          title="Usuń potwierdzenie"
          content={confirmationDialog?.content}
          onCancel={() => setConfirmationDialog(undefined)}
          onConfirm={confirmationDialog?.onConfirm}
        />
      )}
    </Stack>
  );
};

export default Confirmations;
